'use client';

import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useState, useEffect } from 'react';
import ProductCard from './product-card';
import { useStoreUI } from '@/lib/store/use-store-ui';
import { cn } from '@/lib/utils';

interface ProductGridProps {
  products: any[];
}

export default function ProductGrid({ products }: ProductGridProps) {
  const { layoutMode, zoomLevel } = useStoreUI();
  const [mounted, setMounted] = useState(false);
  const [columnCount, setColumnCount] = useState(3);

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);

    const updateColumns = () => {
      const width = window.innerWidth;
      let baseCols = 3;

      if (width < 640) baseCols = 1;
      else if (width < 1024) baseCols = 2;
      else baseCols = 3;

      // Apply zoomLevel adjustment
      let adjustedCols = baseCols;
      if (zoomLevel === -1) {
        adjustedCols = baseCols + 1;
      } else if (zoomLevel === 1) {
        adjustedCols = Math.max(1, baseCols - 1);
      }

      setColumnCount(adjustedCols);
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [zoomLevel]);

  if (!mounted) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((product) => (
          <div key={product.id} className="aspect-[4/5] bg-white/5 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  // Consistent spring for "Cool" but not "Extreme" motion
  const springConfig = {
    type: "spring",
    stiffness: 320,
    damping: 24,
    mass: 1
  } as const;

  // --- UNIFIED GRID / LIST / MASONRY ---
  return (
    <LayoutGroup id="store-grid">
      <motion.div
        layout
        className={cn(
          "w-full transition-all duration-700",
          layoutMode === 'masonry'
            ? "block space-y-6 sm:space-y-8"
            : layoutMode === 'list'
              ? cn(
                  "grid gap-4",
                  zoomLevel === -1 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" :
                  zoomLevel === 0 ? "grid-cols-1 lg:grid-cols-2" :
                  "grid-cols-1"
                )
              : "grid gap-6 sm:gap-8"
        )}
        style={
          layoutMode === 'masonry'
            ? { columnCount }
            : layoutMode === 'grid'
              ? { gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }
              : {}
        }
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              layoutId={`product-${product.id}`}
              layout
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                ...springConfig,
                delay: index * 0.005
              }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  );
}